import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * After the modal is shown, re-sync pickup card from `amb_store_selection`.
   * Same-tab `store-selection:changed` fires on `window` (not `document`); this also covers
   * morphing from empty → non-empty cart where the custom element connected before selection existed.
   */
  showDialog() {
    super.showDialog();
    this.#syncPickupCardsFromStorage();
  }

  /** @param {CartUpdateEvent} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.itemCount !== 0) return;
    this.#syncPickupCardsFromStorage();
  };

  #syncPickupCardsFromStorage() {
    customElements.whenDefined('cart-pickup-store-selection').then(() => {
      requestAnimationFrame(() => {
        for (const el of this.querySelectorAll('cart-pickup-store-selection')) {
          const pickupEl = /** @type {any} */ (el);
          if (typeof pickupEl.syncFromStorage === 'function') {
            pickupEl.syncFromStorage();
          }
        }
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /** @param {CustomEvent} event */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    const source = String(event?.detail?.data?.source || '');
    const didError = Boolean(event?.detail?.data?.didError);
    const shouldAutoOpen = source === 'product-form-component' && !didError;
    if (!shouldAutoOpen) return;

    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
